import { useState } from 'react';
import {
  apiCreateFunnelOffer,
  apiDeleteFunnelOffer,
  apiUpdateFunnelOffer,
  type FunnelDetail,
  type FunnelOffer,
  type FunnelOfferInput,
} from '~/lib/api/funnels';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  Section,
  Segmented,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import {
  apiErrorMessage,
  formatMinor,
  majorToMinor,
  minorToMajorInput,
  recurringLabel,
  stepTypeLabel,
} from './funnels-model';

type Billing = 'once' | 'month' | 'year';

export function OffersEditor({
  appId,
  detail,
  onChanged,
}: {
  appId: string;
  detail: FunnelDetail;
  onChanged: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const currency = detail.funnel.currency;

  const addButton = !creating && (
    <button onClick={() => setCreating(true)} className={BTN_GHOST}>
      <div className="i-ph:plus text-[13px]" />
      New offer
    </button>
  );

  return (
    <Section title="Offers" icon="i-ph:tag" aside={addButton}>
      <p className="text-[11px] leading-relaxed text-muted">
        What each checkout, bump and upsell step sells. Prices are in {currency}.
      </p>

      {creating && (
        <OfferForm
          appId={appId}
          funnelId={detail.funnel.id}
          currency={currency}
          onDone={(saved) => {
            setCreating(false);

            if (saved) {
              onChanged();
            }
          }}
        />
      )}

      {detail.offers.length === 0 ? (
        !creating && (
          <div className={classNames(CARD, 'px-4 py-5 text-center')}>
            <p className="text-[12px] text-muted">No offers yet.</p>
          </div>
        )
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {detail.offers.map((offer) =>
            editing === offer.id ? (
              <div key={offer.id} className="p-1">
                <OfferForm
                  appId={appId}
                  funnelId={detail.funnel.id}
                  currency={currency}
                  offer={offer}
                  onDone={(saved) => {
                    setEditing(null);

                    if (saved) {
                      onChanged();
                    }
                  }}
                />
              </div>
            ) : (
              <OfferRow
                key={offer.id}
                appId={appId}
                offer={offer}
                currency={currency}
                usedBy={detail.steps.filter((s) => s.offerId === offer.id).map((s) => stepTypeLabel(s.type))}
                onEdit={() => setEditing(offer.id)}
                onDeleted={onChanged}
              />
            ),
          )}
        </div>
      )}
    </Section>
  );
}

function OfferRow({
  appId,
  offer,
  currency,
  usedBy,
  onEdit,
  onDeleted,
}: {
  appId: string;
  offer: FunnelOffer;
  currency: string;
  usedBy: string[];
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setBusy(true);
    setError(null);

    try {
      await apiDeleteFunnelOffer(appId, offer.id);
      onDeleted();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete this offer.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium text-primary">{offer.title}</div>
          <div className="text-[11px] text-muted">
            {usedBy.length ? `Used by ${usedBy.join(', ')}` : 'Not used by a step'}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-geist-mono text-[13px] text-primary">{formatMinor(offer.amount, currency)}</div>
          {offer.compareAtAmount !== null && offer.compareAtAmount > offer.amount && (
            <div className="font-geist-mono text-[11px] text-faint line-through">
              {formatMinor(offer.compareAtAmount, currency)}
            </div>
          )}
          <div className="text-[11px] text-muted">{recurringLabel(offer.recurring)}</div>
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-1">
        <button type="button" onClick={onEdit} disabled={busy} className={BTN_GHOST}>
          <div className="i-ph:pencil-simple text-[13px]" />
          Edit
        </button>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy || usedBy.length > 0}
          className={BTN_GHOST}
          title={usedBy.length ? 'Change the steps that use this offer first' : 'Delete offer'}
        >
          {busy ? <Spinner /> : <div className="i-ph:trash text-[13px]" />}
          Delete
        </button>
      </div>
      {error && (
        <div className="mt-2">
          <ErrorBanner message={error} />
        </div>
      )}
    </div>
  );
}

function billingOf(offer?: FunnelOffer): Billing {
  const r = offer?.recurring;

  if (r && (r.intervalCount ?? 1) === 1 && (r.interval === 'month' || r.interval === 'year')) {
    return r.interval;
  }

  return 'once';
}

function OfferForm({
  appId,
  funnelId,
  currency,
  offer,
  onDone,
}: {
  appId: string;
  funnelId: string;
  currency: string;
  offer?: FunnelOffer;
  onDone: (saved: boolean) => void;
}) {
  const [title, setTitle] = useState(offer?.title ?? '');
  const [description, setDescription] = useState(offer?.description ?? '');
  const [amount, setAmount] = useState(offer ? minorToMajorInput(offer.amount, currency) : '');
  const [compareAt, setCompareAt] = useState(
    offer?.compareAtAmount ? minorToMajorInput(offer.compareAtAmount, currency) : '',
  );
  // An unusual existing interval (every 2 weeks…) is kept unless the owner picks another.
  const unusualRecurring = Boolean(offer?.recurring) && billingOf(offer) === 'once';
  const [billing, setBilling] = useState<Billing | 'keep'>(unusualRecurring ? 'keep' : billingOf(offer));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Give the offer a title.');
      return;
    }

    const minor = majorToMinor(amount, currency);

    if (minor === null) {
      setError(`Enter a price above zero in ${currency}.`);
      return;
    }

    const compare = compareAt.trim() ? majorToMinor(compareAt, currency) : null;

    if (compareAt.trim() && compare === null) {
      setError('Compare-at is a price, or blank for none.');
      return;
    }

    const body: FunnelOfferInput = {
      title: title.trim(),
      description: description.trim() || null,
      amount: minor,
      compareAtAmount: compare,
      ...(billing === 'keep' ? {} : { recurring: billing === 'once' ? null : { interval: billing } }),
    };

    setSaving(true);

    try {
      if (offer) {
        await apiUpdateFunnelOffer(appId, offer.id, body);
      } else {
        await apiCreateFunnelOffer(appId, funnelId, body);
      }

      onDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this offer.'));
    } finally {
      setSaving(false);
    }
  };

  const billingOptions: (readonly [Billing | 'keep', string])[] = [
    ['once', 'One-time'],
    ['month', 'Monthly'],
    ['year', 'Yearly'],
  ];

  if (unusualRecurring) {
    billingOptions.push(['keep', recurringLabel(offer?.recurring)]);
  }

  return (
    <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field label="Title">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Masterclass bundle"
          className="ui-input"
        />
      </Field>
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Field label={`Price (${currency})`}>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="999"
              className="ui-input-mono !py-2"
            />
          </Field>
        </div>
        <div className="min-w-0 flex-1">
          <Field label="Compare-at" hint="Optional, shown struck through.">
            <input
              value={compareAt}
              onChange={(e) => setCompareAt(e.target.value)}
              inputMode="decimal"
              placeholder="None"
              className="ui-input-mono !py-2"
            />
          </Field>
        </div>
      </div>
      <div className="space-y-1.5">
        <span className="block text-[11px] text-secondary">Billing</span>
        <Segmented label="Billing" value={billing} options={billingOptions} onChange={setBilling} />
      </div>
      <Field label="Description" hint="Optional.">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="ui-input resize-y"
        />
      </Field>
      {error && <ErrorBanner message={error} />}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className={BTN_SECONDARY}>
          {saving && <Spinner />}
          {saving ? 'Saving…' : offer ? 'Save offer' : 'Create offer'}
        </button>
        <button type="button" onClick={() => onDone(false)} disabled={saving} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
